import * as React from 'react';
import type { Tokens } from '../../../utils/network/types';
import type { OAuthProviderEnum } from '../../../utils/types/eunms';
import { useCognitoContext } from '../../providers/cognito.provider';
import { NetworkClientBuilder, setTokens } from '@utils';

type Props = {
	onSuccess: () => void;
	onError?: (error: Error) => void;
};

/**
 * Checks if the given MessageEvent represents a server-sent error.
 * @param event {MessageEvent} - The MessageEvent to check.
 * @returns {boolean} - True if the event is an error or timeout, false otherwise.
 */
function isServerSentError(event: MessageEvent): boolean {
	return (event.type === 'error' && event.data) || event.type === 'timeout';
}

export const useSSOLogin = ({ onSuccess, onError }: Props) => {
	const { ssoAction, host } = useCognitoContext();

	const [provider, setProvider] = React.useState<OAuthProviderEnum>();
	const [ssoListener, setSsoListener] = React.useState<EventSource>();
	const onSuccessRef = React.useRef(onSuccess);
	const onErrorRef = React.useRef(onError);

	React.useEffect(() => {
		onSuccessRef.current = onSuccess;
	}, [onSuccess]);

	React.useEffect(() => {
		onErrorRef.current = onError;
	}, [onError]);

	const handleSSOLogin = async (selectedProvider: OAuthProviderEnum) => {
		setProvider(selectedProvider);

		const state: string = await ssoAction(selectedProvider);

		setSsoListener(
			new EventSource(`${host}/oauth/sso-events/${state}`, { withCredentials: NetworkClientBuilder.includeCredentials })
		);
	};

	React.useEffect(() => {
		if (!ssoListener) {
			return;
		}

		const controller: AbortController = new AbortController();

		const closeListener = () => {
			ssoListener.close();
			setProvider(undefined);
			controller.abort();
		};

		const onSuccessEvent = (event: MessageEvent) => {
			const tokens: Tokens = JSON.parse(event.data);

			closeListener();

			setTokens(tokens);
			onSuccessRef.current?.();
		};

		const onErrorEvent = (event: MessageEvent) => {
			console.error('Error during SSO login flow:', event);

			if (!isServerSentError(event)) {
				console.error('Error in EventSource', event, 'SSE status:', ssoListener.readyState);
				return;
			}

			closeListener();

			onErrorRef.current?.(new Error(JSON.parse(event.data)?.message));
		};

		const onOpenEvent = () => {
			ssoListener.addEventListener('success', onSuccessEvent, { signal: controller.signal });
			ssoListener.addEventListener('error', onErrorEvent, { signal: controller.signal });
			ssoListener.addEventListener('timeout', onErrorEvent, { signal: controller.signal });
		};

		ssoListener.addEventListener('open', onOpenEvent, { signal: controller.signal });

		return () => {
			if (!ssoListener) {
				return;
			}

			closeListener();
		};
	}, [ssoListener]);

	return { handleSSOLogin, provider };
};
